import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Alert,
  Typography,
  Card,
  CardContent
} from '@mui/material';
import Grid from '../components/common/Grid';
import {
  CheckCircle as CheckIcon,
  NavigateNext as NextIcon
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../hooks/reduxHooks';
import { WorkflowStage, setWaterProfile, setStageValidation } from '../store/slices/workflowSlice';
import WorkflowLayout from '../components/workflow/WorkflowLayout';
import { WaterQualityDashboard } from '../components/water-quality';

const WaterQualityPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [selectedProfile, setSelectedProfile] = useState<any>(null);
  const [analysisResults, setAnalysisResults] = useState<any>(null);

  const { stageStatus } = useAppSelector(state => state.workflow);
  const currentStageStatus = stageStatus[WorkflowStage.WATER_PROFILE];

  const handleWaterProfileSave = useCallback((waterData: any) => {
    console.log('Water profile saved:', waterData);

    // Convert to workflow format
    const workflowWaterProfile = {
      id: waterData.id,
      name: waterData.name || 'Water Profile',
      ph: waterData.ph || 7.0,
      ec: waterData.ec || 0.5,
      elements: [
        { symbol: 'N', concentration: waterData.no3 || 0, unit: 'ppm' },
        { symbol: 'P', concentration: waterData.p || 0, unit: 'ppm' },
        { symbol: 'K', concentration: waterData.k || 0, unit: 'ppm' },
        { symbol: 'Ca', concentration: waterData.ca || 0, unit: 'ppm' },
        { symbol: 'Mg', concentration: waterData.mg || 0, unit: 'ppm' },
        { symbol: 'S', concentration: waterData.s || 0, unit: 'ppm' },
        { symbol: 'Na', concentration: waterData.na || 0, unit: 'ppm' },
        { symbol: 'Cl', concentration: waterData.cl || 0, unit: 'ppm' }
      ].filter(e => e.concentration > 0)
    };

    setSelectedProfile(workflowWaterProfile);

    // Update workflow state
    dispatch(setWaterProfile(workflowWaterProfile));

    // Validate the stage
    const warnings = [];
    if (workflowWaterProfile.elements.length === 0) {
      warnings.push('No background elements detected - calculation will proceed without water quality adjustments');
    }
    if (workflowWaterProfile.ph < 5.5 || workflowWaterProfile.ph > 7.5) {
      warnings.push('pH value outside typical hydroponic range (5.5-7.5)');
    }
    if (workflowWaterProfile.ec > 2.0) {
      warnings.push('High EC value may require water dilution');
    }

    dispatch(setStageValidation({
      stage: WorkflowStage.WATER_PROFILE,
      validated: true,
      warnings
    }));
  }, [dispatch]);

  const handleAnalysisComplete = useCallback((results: any) => {
    console.log('Water quality analysis completed:', results);
    setAnalysisResults(results);
  }, []);

  const handleProceedToFormulation = () => {
    navigate('/formulation');
  };

  const canProceed = currentStageStatus.completed && currentStageStatus.validated;

  return (
    <WorkflowLayout
      stage={WorkflowStage.WATER_PROFILE}
      title="Water"
      description="Source water ions, pH, and EC used as background in the nutrient balance."
    >
      <WaterQualityDashboard />

      {/* Stage Completion Summary */}
      {selectedProfile && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CheckIcon color="success" />
              <Typography variant="h6">
                Water Profile Selected: {selectedProfile.name}
              </Typography>
            </Box>

            <Grid container spacing={2}>
              <Grid xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  <strong>pH:</strong> {selectedProfile.ph}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>EC:</strong> {selectedProfile.ec} mS/cm
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Background Elements:</strong> {selectedProfile.elements.length} detected
                </Typography>
              </Grid>

              <Grid xs={12} md={6}>
                {selectedProfile.elements.length > 0 && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Element Concentrations (ppm):</strong>
                    </Typography>
                    {selectedProfile.elements.map((element: any, index: number) => (
                      <Typography key={index} variant="caption" display="block">
                        {element.symbol}: {element.concentration}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Grid>
            </Grid>

            {analysisResults && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Analysis Complete:</strong> Water quality score: {analysisResults.overallScore || 'N/A'}
                </Typography>
              </Alert>
            )}

            {canProceed && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <Button
                  variant="contained"
                  endIcon={<NextIcon />}
                  onClick={handleProceedToFormulation}
                  size="large"
                >
                  Proceed to Crop Formulation
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Instructions */}
      {!selectedProfile && (
        <Alert severity="info" sx={{ mt: 3 }}>
          <Typography variant="body2">
            <strong>Instructions:</strong>
          </Typography>
          <Typography variant="body2" component="div" sx={{ mt: 1 }}>
            1. Use the "Input & Setup" tab to enter your water quality parameters<br/>
            2. Save the water profile to the database<br/>
            3. Use the "Analysis & Reports" tab to analyze the water quality<br/>
            4. Confirm your selection to proceed to the next stage
          </Typography>
        </Alert>
      )}
    </WorkflowLayout>
  );
};

export default WaterQualityPage;
